import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';

import { makeStyles } from '@material-ui/core/styles';
import {
    Link,
    Typography,
    FormControlLabel,
    Checkbox,
    InputAdornment,
    IconButton
} from '@material-ui/core';
import PersonIcon from '@material-ui/icons/Person';
import PasswordIcon from '@material-ui/icons/VpnKey';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';

import AuthBody from '../AuthBody';
import AuthBodyTitle from '../AuthBodyTitle';
import AuthBodyConfirmation from '../AuthBodyConfirmation';
import AuthBodyAlert from '../AuthBodyAlert';
import AuthBodyTextFieldRow from '../AuthBodyTextFieldRow';
import AuthBodyTextField from '../AuthBodyTextField';
import AuthBodyActions from '../AuthBodyActions';
import AuthBodyButton from '../AuthBodyButton';

const propTypes = {
    setConfirmEmail: PropTypes.func.isRequired,
    message: PropTypes.string.isRequired
};

const useStyles = makeStyles((theme) => styles(theme));

const DEMO_URL = "https://landhawk.hubspotpagebuilder.com/book-a-landhawk-demo";

function LoginBody ({ setConfirmEmail, message }) {
    const classes = useStyles();
    const authenticity_token = document.querySelector('meta[name=\'csrf-token\']').getAttribute('content');

    const [redirectRoute, setRedirectRoute] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(0);
    const [passwordRevealed, setPasswordRevealed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    function togglePasswordReveal () {
        setPasswordRevealed(!passwordRevealed);
    }

    function sendUnlockInstructions () {
        const searchParams = new URLSearchParams({
            authenticity_token: authenticity_token,
            utf8: '✓',
            commit: 'RESEND unlock instructions',
            'user[email]': email
        });
        fetch('/user/unlock', {
            method: 'POST',
            body: searchParams.toString(),
            headers: {
                'content-type': 'application/x-www-form-urlencoded'
            }
        })
            .then((res) => res.json())
            .then((data) => {
                setRedirectRoute('/user/unlock/new');
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
            });
    }

    function handleSubmitForm (e) {
        e.preventDefault();
        setLoading(true);
        const searchParams = new URLSearchParams({
            authenticity_token: authenticity_token,
            utf8: '✓',
            commit: 'LOGIN',
            'user[email]': email,
            'user[password]': password,
            'user[remember_me]': rememberMe
        });
        fetch('/user/login', {
            method: 'POST',
            body: searchParams.toString(),
            headers: {
                'content-type': 'application/x-www-form-urlencoded'
            }
        })
            .then((res) => res.json())
            .then((data) => {
                if (data.success) {
                    window.location.replace('/app/map');
                } else {
                    if (data.locked) {
                        sendUnlockInstructions();
                    } else {
                        setError(data.message);
                        setLoading(false);
                        if (data.message === 'User is unverified') {
                            setRedirectRoute('/user/confirmation_sent');
                            setConfirmEmail(email);
                        }
                    }
                }
            })
            .catch((err) => {
                setError(err.response.message);
            });
    }

    return (
        <AuthBody onSubmit={handleSubmitForm}>
            { redirectRoute !== '' ? <Redirect to={redirectRoute} /> : null }
            <div className={classes.titleContainer}>
                <AuthBodyTitle>
                    Welcome back
                </AuthBodyTitle>
                <AuthBodyConfirmation
                    linkText="Book a demo"
                    href={DEMO_URL}
                    LinkProps={{
                        target: "_blank"
                    }}
                >
                    Don't have an account?
                </AuthBodyConfirmation>
            </div>
            { Boolean(message) && <AuthBodyAlert severity="success" >{ message }</AuthBodyAlert> }
            { Boolean(error) && <AuthBodyAlert severity="error" >{ error }</AuthBodyAlert> }
            <div>
                <AuthBodyTextFieldRow 
                    icon={<PersonIcon color="primary" />}
                >
                    <AuthBodyTextField
                        fullWidth
                        label="Email"
                        color="primary"
                        name="user[email]"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                </AuthBodyTextFieldRow>
                <AuthBodyTextFieldRow 
                    icon={<PasswordIcon color="primary" />}
                >
                    <AuthBodyTextField
                        fullWidth
                        label="Password"
                        color="primary"
                        type={!passwordRevealed ? 'password' : 'text'}
                        name="user[password]"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        classes={{ root: classes.endAdornmentIconButtonRoot }}
                                        onClick={togglePasswordReveal}
                                    >
                                        { passwordRevealed ? <VisibilityIcon color="primary" /> : <VisibilityOffIcon color="primary" /> }
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                </AuthBodyTextFieldRow>
            </div>
            <AuthBodyActions>
                <AuthBodyButton
                    type="submit"
                    disabled={loading}
                >
                    Log In
                </AuthBodyButton>
                <FormControlLabel
                    classes={{ root: classes.checkboxFormControlLabelRoot}}
                    control={
                        <Checkbox
                            value={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked ? 1 : 0)}
                            name="user[remember_me]"
                        />
                    }
                    label="Remember me"
                />
                <Typography variant="body2">
                    <Link 
                        classes={{ root: classes.link }}
                        href="/user/password/new" 
                        underline="always"
                    >
                        Forgot Password?
                    </Link>
                </Typography>
            </AuthBodyActions>
        </AuthBody>
    );
}

const styles = (theme) => ({
    titleContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline'
    },
    checkboxFormControlLabelRoot: {
        marginLeft: '5px',
        '& > .MuiFormControlLabel-label': {
            fontSize: '14px',
            color: theme.palette.grey.variant3
        },
        '& > span > span > svg': {
            width: '20px',
            height: '20px'
        }
    },
    endAdornmentIconButtonRoot: {
        padding: 0
    },
    link: {
        color: theme.palette.grey.variant3
    }
});

LoginBody.propTypes = propTypes;

export default LoginBody;